import React, { useState } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Translate } from 'react-localize-redux';
import { AutoComplete, Datepicker } from '../../components/widgets';
import Icon, { IconSizes, IconVariants } from '../../components/foundations/Icon';
import { formatStartDate } from '../utils';
import { TRANSLATION_PREFIX, VALUE_CURRENCY_FIELD } from '../consts';
import css from '../page.css';

const ALWAYS_LABEL = `${TRANSLATION_PREFIX}.ALWAYS`;

// A dictionary-backed grid cell. Uses the bare AutoComplete (not AutoCompleteField) so the cell
// has no label/error-text rows — errors surface as a red border, like the legacy grid.
const DictCell = ({ field, placeholder, cell, options, dictionary, error, disabled, isClearable = true, onChange }) => (
    <AutoComplete
        // Remount when the resolved cell changes so the control reflects external updates
        // (import, reload, row deletion shift) without becoming a stale controlled input.
        key={`${field}-${cell ? cell.name : ''}`}
        placeholder={placeholder}
        defaultValue={cell ? { value: cell.name, label: cell.display_name } : null}
        onChange={option => onChange(field, option, dictionary)}
        error={error ? 'error' : ''}
        searchable
        selectOptions={{
            options,
            disabled,
            isClearable,
            isSearchable: true,
            noOptionsMessage: () => null,
            // Portal the menu to <body> so it isn't clipped by the grid's overflow / z-index,
            // and open it upward when there's no room below (e.g. the last rows).
            usePortal: true,
            menuPlacement: 'auto',
        }}
    />
);

DictCell.propTypes = {
    field: PropTypes.string.isRequired,
    placeholder: PropTypes.string.isRequired,
    cell: PropTypes.shape({ name: PropTypes.string, display_name: PropTypes.string }),
    options: PropTypes.arrayOf(PropTypes.object),
    dictionary: PropTypes.arrayOf(PropTypes.object),
    error: PropTypes.bool,
    disabled: PropTypes.bool,
    isClearable: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
};

function ReferenceRow({
    row,
    visibleColumns,
    dictionaries,
    dictionaryOptions,
    isValueCurrencyEnabled,
    errors,
    disabled,
    deleteDisabled,
    onCellChange,
    onDateChange,
    onValueChange,
    onDelete,
}) {
    const [editingDate, setEditingDate] = useState(false);
    const rowErrors = errors || {};

    // Datepicker builds its calendar from moment(startDate); an empty value renders a NaN calendar,
    // so a dateless row opens on today. The row value stays null until a date is applied.
    const pickerDate = row.startDate || moment().format('YYYY-MM-DD');

    const deleteButton = (
        <button
            type="button"
            className={css.deleteButton}
            title="Delete row"
            disabled={deleteDisabled}
            onClick={onDelete}
        >
            <Icon name="trash" variant={IconVariants.LIGHT} size={IconSizes.SMALL} />
        </button>
    );

    return (
        <tr className={css.gridRow}>
            <td className={css.dateCell}>
                <div className={css.dateCellInner}>
                    {editingDate ? (
                        <>
                            <Datepicker
                                singleDatePicker
                                autoApply
                                ranges={{}}
                                containerClass={css.datePicker}
                                startDate={pickerDate}
                                endDate={pickerDate}
                                onApply={({ start_date: startDate }) => {
                                    onDateChange(startDate);
                                    setEditingDate(false);
                                }}
                            />
                            <button
                                type="button"
                                className={css.dateAlwaysButton}
                                title="Clear the date (rule applies always)"
                                onClick={() => {
                                    onDateChange(null);
                                    setEditingDate(false);
                                }}
                            >
                                <Translate id={ALWAYS_LABEL} />
                            </button>
                        </>
                    ) : (
                        <span className={css.dateText}>
                            {row.startDate ? formatStartDate(row.startDate, '') : <Translate id={ALWAYS_LABEL} />}
                        </span>
                    )}
                    <button
                        type="button"
                        className={css.editDateButton}
                        title={editingDate ? 'Done' : 'Edit date'}
                        disabled={disabled}
                        onClick={() => setEditingDate(editing => !editing)}
                    >
                        <Icon
                            name={editingDate ? 'check' : 'pencil'}
                            variant={IconVariants.LIGHT}
                            size={IconSizes.SMALL}
                        />
                    </button>
                </div>
            </td>

            {visibleColumns.map(column => (
                <td key={column.field} className={css.dictCell}>
                    <DictCell
                        field={column.field}
                        placeholder={column.placeholder}
                        cell={row[column.field]}
                        options={dictionaryOptions[column.dict]}
                        dictionary={dictionaries[column.dict]}
                        error={!!rowErrors[column.field]}
                        disabled={disabled}
                        onChange={onCellChange}
                    />
                </td>
            ))}

            <td className={css.valueCell}>
                <input
                    className={`${css.valueInput} ${rowErrors.value ? css.inputError : ''}`}
                    type="number"
                    step="0.01"
                    placeholder="Enter value"
                    disabled={disabled}
                    value={row.value}
                    onChange={event => onValueChange(event.target.value)}
                />
            </td>

            {isValueCurrencyEnabled && (
                <td className={`${css.dictCell} ${css.valueCurrencyCell}`}>
                    <DictCell
                        field={VALUE_CURRENCY_FIELD}
                        placeholder={`${TRANSLATION_PREFIX}.COLUMNS.CURRENCY`}
                        cell={row[VALUE_CURRENCY_FIELD]}
                        options={dictionaryOptions[VALUE_CURRENCY_FIELD]}
                        dictionary={dictionaries[VALUE_CURRENCY_FIELD]}
                        error={!!rowErrors[VALUE_CURRENCY_FIELD]}
                        disabled={disabled}
                        // Required field — no clear control (empty fails validation and it crowds the cell).
                        isClearable={false}
                        onChange={onCellChange}
                    />
                </td>
            )}

            {/* Delete sits in its own borderless column, outside the grid, so it doesn't read as a cell action. */}
            <td className={css.actionColumn}>{deleteButton}</td>
        </tr>
    );
}

ReferenceRow.propTypes = {
    row: PropTypes.shape({
        _key: PropTypes.string,
        id: PropTypes.number,
        startDate: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }).isRequired,
    visibleColumns: PropTypes.arrayOf(
        PropTypes.shape({ field: PropTypes.string, placeholder: PropTypes.string, dict: PropTypes.string })
    ).isRequired,
    dictionaries: PropTypes.objectOf(PropTypes.array).isRequired,
    dictionaryOptions: PropTypes.objectOf(PropTypes.array).isRequired,
    isValueCurrencyEnabled: PropTypes.bool,
    errors: PropTypes.objectOf(PropTypes.bool),
    disabled: PropTypes.bool,
    deleteDisabled: PropTypes.bool,
    onCellChange: PropTypes.func.isRequired,
    onDateChange: PropTypes.func.isRequired,
    onValueChange: PropTypes.func.isRequired,
    onDelete: PropTypes.func.isRequired,
};

export default ReferenceRow;
